import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { AppShell } from './components/AppShell';
import { Spinner } from './components/ui';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { DealsPage } from './pages/DealsPage';
import { NewDealPage } from './pages/NewDealPage';
import { ConfirmPage } from './pages/ConfirmPage';
import { LedgerPage } from './pages/LedgerPage';
import { PartiesPage } from './pages/PartiesPage';
import { ProgrammesPage } from './pages/ProgrammesPage';
import { UsersPage } from './pages/UsersPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // A 401 is handled by the client's refresh-and-replay; a 403 or 404 will
      // never succeed on retry, so only retry once and only for other errors.
      retry: (count, error) => {
        const status = (error as { status?: number }).status;
        if (status && status >= 400 && status < 500) return false;
        return count < 1;
      },
    },
  },
});

function Routed() {
  const { session, loading } = useAuth();

  if (loading) {
    return (
      <div className="login-wrap">
        <Spinner label="Restoring your session" />
      </div>
    );
  }

  // The confirmation link is deliberately outside the session gate: a payer
  // following it has no account, and bouncing them to a login screen would
  // make the whole flow pointless.
  if (!session) {
    return (
      <Routes>
        <Route path="/confirm/:token" element={<ConfirmPage />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/confirm/:token" element={<ConfirmPage />} />
      <Route element={<AppShell />}>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/deals" element={<DealsPage />} />
        <Route path="/deals/new" element={<NewDealPage />} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="/ledger" element={<LedgerPage />} />
        <Route path="/parties" element={<PartiesPage />} />
        <Route path="/programmes" element={<ProgrammesPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routed />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
